import {Schema, type ResolvedPos, type MarkType, DOMSerializer, DOMParser, type Fragment} from "prosemirror-model";
import {type Command, type Transaction, Plugin, EditorState, TextSelection} from "prosemirror-state";
import {toggleMark, wrapIn, setBlockType } from "prosemirror-commands";
import {buildSchema} from "@fd/editors/html/schema/SchemaBuilder.ts";
import {ref, type Ref} from "vue";
import {linkClickPlugin} from "@fd/editors/html/plugins/LinkClickPlugin.ts";
import {createSelectionListenerPlugin, UpdateToolbarCallback} from "@fd/editors/html/plugins/SelectionListenerPlugin.ts";
import {addLinkCommand, LinkData, removeLinkCommand} from "@fd/editors/html/commands/Links.ts";
import {FormDefinition} from "@t/fd/layout/FormDefinition.ts";
import {FormEnvironment} from "@fd/Types.ts";
import selectionDecorationPlugin from "@fd/editors/html/plugins/SelectionDecorationPlugin.ts";
import {createToolbarUpdatePlugin} from "@fd/editors/html/plugins/ToolbarUpdatePlugin.ts";
import { wrapInList, liftListItem, sinkListItem } from "prosemirror-schema-list";
import {mutating} from "@fd/editors/html/commands/Mutating.ts";
import {wrapInDescList} from "@fd/editors/html/commands/Lists.ts";
import {keyboardSupport} from "@fd/editors/html/plugins/Keymap.ts";
import {history} from "prosemirror-history";
import {insertHorizontalRule} from "@fd/editors/html/commands/HardBreak.ts";
import {tableEditing, columnResizing, goToNextCell, addColumnBefore, addColumnAfter, addRowBefore, addRowAfter,
	deleteRow, deleteColumn, mergeCells, splitCell, deleteTable, toggleHeaderRow, toggleHeaderColumn, toggleHeaderCell } from "prosemirror-tables";
import {insertTableCommand} from "@fd/editors/html/commands/Table.ts";
import {ImageData, insertImageCommand} from "@fd/editors/html/commands/Image.ts";
import {gapCursor} from "prosemirror-gapcursor";
import {dropCursor} from "prosemirror-dropcursor";
import {InterfaceRoot} from "@t/fd/InterfaceRoot.ts";

const DEFAULT_ALIGNMENT = "left";

export interface UiCommand<D> {
	/** Extract data object from the editor state (or init) */
	extract: (state:EditorState) => D;
	/** Structure of the form which will be displayed */
	form: InterfaceRoot<FormDefinition>;
	/** If the dialog is confirmed with OK this method is calle to obtain ProseMirror command */
	prepare: (data:D, savedSelection?:any) => Command;
}



function setTextAlignment(align:"left"|"center"|"right"|"justify"):Command {
	return (state: EditorState, dispatch?: (tr: Transaction) => void) => {
		const { selection, schema } = state;
		const { from, to } = selection;
		const { nodes } = schema;

		// Adjust this to match your schema's block nodes
		const blockTypes = [nodes.paragraph, nodes.heading];

		const applicableBlocks = [];
		state.doc.nodesBetween(from, to, (node, pos) => {
			if (blockTypes.includes(node.type)) {
				applicableBlocks.push({ node, pos });
			}
		});
		if (!applicableBlocks.length) return false;

		if (dispatch) {
			const tr = state.tr;
			applicableBlocks.forEach(({ node, pos }) => {
				const newAttrs = { ...node.attrs, textAlign: align };

				if (align === DEFAULT_ALIGNMENT) {
					// Remove the attribute if it matches the default
					delete newAttrs.textAlign;
				}

				tr.setNodeMarkup(pos, node.type, newAttrs, node.marks);
			});

			dispatch(tr.scrollIntoView());
		}
		return false;
	};
}


function isMark(state:EditorState, $from:ResolvedPos, markType:MarkType) {
	const marks = state.schema.marks;
	return state.storedMarks?.some((mark) => mark.type === markType) ||
	$from.marks().some((mark) => mark.type === markType);
}


export function editorSupport(env:FormEnvironment, toolbarCallback?:UpdateToolbarCallback): {
	schema:Schema,
	bold:Command, isBold:Ref<boolean>,
	italic:Command, isItalic:Ref<boolean>,
	underline:Command, isUnderline:Ref<boolean>,
	strike:Command, isStrike:Ref<boolean>,
	subscript:Command, isSubscript:Ref<boolean>,
	superscript:Command, isSuperscript:Ref<boolean>,
	mark:Command, isMarked:Ref<boolean>,
	removeColor:Command,
	setColor: (c:string) => Command,
	alignLeft:Command, alignCenter:Command, alignRight:Command, alignJustify:Command,
	blockType: Ref<string>,
	selectionWithLinks:Ref<boolean>, selectionEmpty:Ref<boolean>,
	play:Command,
	unlink:Command,
	link:UiCommand<LinkData>,
	orderedList: Command, canOrderedList: Ref<boolean>,
	bulletList: Command, canBulletList: Ref<boolean>,
	descList: Command, canDescList: Ref<boolean>,
	outdent: Command, canOutdent: Ref<boolean>,
	indent: Command, canIndent: Ref<boolean>,
	blockquote:Command, canBlockquote: Ref<boolean>,
	insertHorizontalRule:Command,
	insertTable:Command,
	deleteTable:Command,
	addColumnBefore:Command,
	addColumnAfter:Command,
	addRowBefore:Command, addRowAfter:Command,
	deleteRow:Command, deleteColumn:Command,
	mergeCells:Command, splitCell:Command, canMergeCells:Ref<boolean>, canSplitCell:Ref<boolean>,
	toggleHeaderRow:Command, toggleHeaderColumn:Command, toggleHeaderCell:Command,
	canInsertTable:Ref<boolean>, canDeleteTable:Ref<boolean>,
	modifyBlockType:(type:string, attrs:Record<string, any>) => Command,
	insertImage:UiCommand<ImageData>,
	plugins:Plugin[],
	getSelectedHTML: (state: EditorState) => string,
	insertHTML: (html:string) => Command
} {
	/** Add my own NODES */
	const schema = buildSchema();

	const bold: Command = (state: EditorState, dispatch?: (tr: Transaction) => void) => {
		toggleMark(state.schema.marks.strong)(state, dispatch);
		return true;
	};

	const italic: Command = (state: EditorState, dispatch?: (tr: Transaction) => void) => {
		toggleMark(state.schema.marks.em)(state, dispatch);
		return true;
	};

	const underline: Command = (state: EditorState, dispatch?: (tr: Transaction) => void) => {
		toggleMark(state.schema.marks.underline)(state, dispatch);
		return true;
	};

	const strike: Command = (state: EditorState, dispatch?: (tr: Transaction) => void) => {
		toggleMark(state.schema.marks.strike)(state, dispatch);
		return true;
	};

	const subscript: Command = (state: EditorState, dispatch?: (tr: Transaction) => void) => {
		toggleMark(state.schema.marks.subscript)(state, dispatch);
		return true;
	};

	const superscript: Command = (state: EditorState, dispatch?: (tr: Transaction) => void) => {
		toggleMark(state.schema.marks.superscript)(state, dispatch);
		return true;
	};

	const mark: Command = (state: EditorState, dispatch?: (tr: Transaction) => void) => {
		toggleMark(state.schema.marks.mark)(state, dispatch);
		return true;
	};

	const removeColor: Command = (state: EditorState, dispatch?: (tr: Transaction) => void) => {
		let {empty, from, to} = state.selection as TextSelection
		const markType = state.schema.marks.fc;

		const tr = state.tr;
		if (empty) {
			tr.removeStoredMark(markType);
		} else {
			tr.removeMark(from, to, state.schema.marks.fc);
		}
		if (dispatch) dispatch(tr.scrollIntoView());
		return true;
	};

	function setColor(color:string):Command {
		if (color === "transparent") return removeColor;
		return (state: EditorState, dispatch?: (tr: Transaction) => void) => {
			//debugger;
			const { selection } = state;
			const { from, to, empty } = selection as TextSelection;
			const markType = state.schema.marks.fc;

			const tr = state.tr;
			if (empty) {
				tr.addStoredMark(markType.create({ color }));
			} else {
				tr.addMark(from, to, markType.create({ color }));
			}
			if (dispatch) dispatch(tr.scrollIntoView());
			return true;
		};
	}

	const alignLeft: Command = setTextAlignment("left");
	const alignCenter: Command = setTextAlignment("center");
	const alignRight: Command = setTextAlignment("right");
	const alignJustify: Command = setTextAlignment("justify");

	const play:Command = (state: EditorState, dispatch?: (tr: Transaction) => void) => {
		const tr = state.tr;
		const from = state.selection.from;
		const $f = state.selection.$from;

		tr.insertText("ANNECA ("+$f.parent.type.name+"-"+$f.depth+") ");
		const newSelection = TextSelection.create(tr.doc, from, from + 6);
		tr.setSelection(newSelection);
		if (dispatch) dispatch(tr);
		return false;
	};


	const isBold = ref<boolean>(false);
	const isItalic = ref<boolean>(false);
	const isUnderline = ref<boolean>(false);
	const isStrike = ref<boolean>(false);
	const isSubscript = ref<boolean>(false);
	const isSuperscript = ref<boolean>(false);
	const isMarked = ref<boolean>(false);
	const canBulletList = ref<boolean>(false);
	const canOrderedList = ref<boolean>(false);
	const canDescList = ref<boolean>(false);
	const canIndent = ref<boolean>(false);
	const canOutdent = ref<boolean>(false);
	const canBlockquote = ref<boolean>(false);

	const blockType = ref<string>("p");
	const selectionWithLinks = ref<boolean>(false);
	const selectionEmpty = ref<boolean>(true);

	const unlink = removeLinkCommand();
	const link = addLinkCommand(env);
	const insertImage = insertImageCommand(env);
	const modifyBlockType = (type:string, attrs:Record<string, any>):Command => {
		return (state, dispatch) => {
			const nodeType = schema.nodes[type];
			if (nodeType) {
				return mutating(setBlockType(nodeType, attrs))(state, dispatch);
				//return mutating(changeBlockType(nodeType, attrs))(state, dispatch);
			}
			return false;
		};
	};
	const orderedList = wrapInList(schema.nodes.ol);
	const bulletList = wrapInList(schema.nodes.ul);
	const descList = wrapInDescList(schema.nodes.dl, schema.nodes.dt, schema.nodes.dd);
	const outdent = liftListItem(schema.nodes.li);
	const indent = sinkListItem(schema.nodes.li);
	const blockquote:Command = wrapIn(schema.nodes.blockquote);
	// Table
	const nextCell: Command = goToNextCell(1);
	const prevCell: Command = goToNextCell(-1);

	const insertTable = insertTableCommand(2, 3);
	const canInsertTable = ref<boolean>(true);
	const canDeleteTable = ref<boolean>(false);
	const canMergeCells = ref<boolean>(false);
	const canSplitCell = ref<boolean>(false);

	// Example toolbar update function
	function updateToolbar(state: EditorState):void {
		const { selection } = state;
		const { $from, empty, from , to } = selection; // $from provides the context of the selection
		const schema = state.schema;

		// Check for active marks or node types
		isBold.value = isMark(state, $from, schema.marks.strong);
		isItalic.value = isMark(state, $from, schema.marks.em);
		isUnderline.value = isMark(state, $from, schema.marks.underline);
		isStrike.value = isMark(state, $from, schema.marks.strike);
		isSubscript.value = isMark(state, $from, schema.marks.subscript);
		isSuperscript.value = isMark(state, $from, schema.marks.superscript);
		isMarked.value = isMark(state, $from, schema.marks.mark);

		if ($from.parent.type === schema.nodes.heading) {
			blockType.value = "h"+$from.parent.attrs.level;
		} else if ($from.parent.type === schema.nodes.paragraph) {
			blockType.value = "p";
		} else if ($from.parent.type === schema.nodes.code_block) {
			blockType.value = "pre";
		} else if ($from.parent.type === schema.nodes.dt) {
			blockType.value = "dt";
		} else if ($from.parent.type === schema.nodes.dd) {
			blockType.value = "dd";
		} else {
			blockType.value = $from.parent.type.name;
		}

		const linkType = schema.marks.link;
		if (linkType) {
			let hasLink = false;
			state.doc.nodesBetween(from, to, (node) => {
				if (hasLink) return false;
				if (node.marks.some((m) => m.type === linkType)) {
					hasLink = true;
				}
			});
			selectionWithLinks.value = hasLink;
		} else selectionWithLinks.value = false;
		selectionEmpty.value = empty;

		// Block buttons - tests
		canBulletList.value = bulletList(state);
		canOrderedList.value = orderedList(state);
		canDescList.value = descList(state);
		canIndent.value = indent(state);
		canOutdent.value = outdent(state);
		canBlockquote.value = blockquote(state);

		canDeleteTable.value = deleteTable(state);
		canMergeCells.value = mergeCells(state);
		canSplitCell.value = splitCell(state);

		if (toolbarCallback) toolbarCallback(state);
	}

	function getSelectedHTML(state: EditorState): string {
		const { selection } = state;

		if (selection.empty) {
			return ""; // Pokud není nic vybráno, vracíme prázdný řetězec
		}

		const fragment = selection.content().content;
		const div = document.createElement("div");

		// Serializujeme fragment do DOM
		fragment.forEach((node) => {
			const serializer = DOMSerializer.fromSchema(state.schema);
			div.appendChild(serializer.serializeNode(node));
		});

		return div.innerHTML;
	}

	function insertHTML(templateText: string):Command {
		return (state: EditorState, dispatch?: (tr: Transaction) => void) => {
			const { schema, selection } = state;
			const { from, to } = selection;

			// Create a temporary container to parse HTML
			const tempDiv = document.createElement("div");
			tempDiv.innerHTML = templateText;

			// Convert HTML to ProseMirror document fragment
			const nodes = DOMParser.fromSchema(schema).parse(tempDiv).content as Fragment;

			if (!nodes.size) return false; // Do nothing if empty

			if (dispatch) {
				dispatch(state.tr.replaceWith(from, to, nodes).scrollIntoView());
			}

			return true;
		};
	}

	const plugins: Plugin[] = [
		history(),
		createSelectionListenerPlugin(updateToolbar),
		...keyboardSupport({
			bold, italic, underline,
			nextCell, prevCell
		}),
		linkClickPlugin(),
		/* selectionDecorationPlugin, */
		columnResizing(),
		tableEditing(),
		gapCursor(),
		dropCursor(),
		createToolbarUpdatePlugin(updateToolbar)	// Listen for mutating transactions
	];

	return {
		schema,
		bold, italic, underline, strike, subscript, superscript, mark,
		removeColor, setColor,
		isBold, isItalic, isUnderline, isStrike, isSubscript, isSuperscript, isMarked,
		blockType,
		alignLeft, alignCenter, alignRight, alignJustify,
		selectionWithLinks, selectionEmpty,
		link, unlink,
		orderedList, canOrderedList,
		bulletList, canBulletList,
		descList, canDescList,
		outdent, canOutdent,
		indent, canIndent,
		modifyBlockType,
		blockquote, canBlockquote,
		insertHorizontalRule,
		insertTable,
		addColumnBefore, addColumnAfter, addRowBefore, addRowAfter,
		deleteRow, deleteColumn, mergeCells, splitCell, deleteTable,
		toggleHeaderRow, toggleHeaderColumn, toggleHeaderCell,
		canInsertTable, canDeleteTable, canMergeCells, canSplitCell,
		insertImage,
		play, plugins,
		getSelectedHTML,
		insertHTML
	}
}